// Concierge Mode CSS declaration blocks, shared verbatim by the two surfaces
// that must render identically:
//
//   - apps/marketplace/src/concierge/ (production: concierge-variables.ts
//     wraps the scheme blocks, concierge-theme.ts wraps the canvas blocks)
//   - apps/admin/src/dispensary-admin/customize/theme/concierge-preview/
//     (the Customize → Theme live preview)
//
// Each export is a selector-less declaration list; consumers wrap the blocks
// in their own scoping selectors. Because both surfaces interpolate the same
// strings, the browser computes the same derived tokens (including every
// color-mix/relative-color formula) in the preview as in production — values
// cannot drift between them.

// TIER 1 primitives + TIER 2a concierge aliases. Production scope: `:root`.
export const CONCIERGE_PRIMITIVES_CSS = `
    /* ================================================================
       TIER 1: PRIMITIVES
       Raw color values. Never consumed directly by components.
       ================================================================ */

    /* AI gold ramp */
    --dcm-concierge-gold-50:   #f6e9c4;
    --dcm-concierge-gold-200:  #ebd089;
    --dcm-concierge-gold-400:  #d4a843; /* base — oklch(75% 0.10 80) */
    --dcm-concierge-gold-500:  #c39a3a;
    --dcm-concierge-gold-600:  #b88e35; /* hover / pressed */
    --dcm-concierge-gold-800:  #6c5320;

    /* Forest — recessive ink + hairline (~160° hue, low chroma). */
    --dcm-concierge-forest-50:   #f1f3f2;
    --dcm-concierge-forest-200:  #889591;
    --dcm-concierge-forest-400:  #5e6e69;
    --dcm-concierge-forest-600:  #1f332c;
    --dcm-concierge-forest-700:  #182f28;
    --dcm-concierge-forest-750:  #142922;
    --dcm-concierge-forest-800:  #10231d;
    --dcm-concierge-forest-900:  #0e201a;
    --dcm-concierge-forest-950:  #0b1914;

    /* Cream — light canvas */
    --dcm-concierge-cream-50:   #fdf9f0;
    --dcm-concierge-cream-100:  #f8efd9;
    --dcm-concierge-cream-200:  #efe2bd;

    /* ================================================================
       TIER 2a: CONCIERGE ALIASES
       Role-named tokens. The single tenant override hook lives here.
       ================================================================ */

    /* AI accent family. -deep/-soft/-glow derive via color-mix; re-declared
       under the scheme scope so they pick up accent overrides applied at the
       wrapper (CSS vars resolve at declaration origin, not use site). */
    --dcm-concierge-accent:        var(--dcm-concierge-gold-400);
    --dcm-concierge-accent-deep:   color-mix(in oklch, var(--dcm-concierge-accent) 85%, black);
    --dcm-concierge-accent-soft:   color-mix(in oklch, var(--dcm-concierge-accent) 30%, transparent);
    --dcm-concierge-accent-glow:   color-mix(in oklch, var(--dcm-concierge-accent) 15%, transparent);
    --dcm-concierge-accent-muted:  color-mix(in oklch, var(--dcm-concierge-accent) 55%, var(--dcm-color-text-secondary));

    /* Neutral popover/card shadow — depth without an "AI-made" gold halo. */
    --dcm-concierge-shadow-soft:   rgba(0, 0, 0, 0.35);
`;

// TIER 2b marketplace alias overrides — the base (dark) scheme. Production
// scope: `.concierge-mode`. This block applies in BOTH schemes; the light
// block below overlays its deltas on top.
export const CONCIERGE_BASE_SCHEME_CSS = `
    /* Re-declare derived accents at this scope so wrapper-level
       --dcm-concierge-accent overrides propagate (CSS vars resolve at
       declaration origin, not use site). */
    --dcm-concierge-accent-deep:   color-mix(in oklch, var(--dcm-concierge-accent) 85%, black);
    --dcm-concierge-accent-soft:   color-mix(in oklch, var(--dcm-concierge-accent) 30%, transparent);
    --dcm-concierge-accent-glow:   color-mix(in oklch, var(--dcm-concierge-accent) 15%, transparent);
    --dcm-concierge-accent-muted:  color-mix(in oklch, var(--dcm-concierge-accent) 55%, var(--dcm-color-text-secondary));

    /* Primary action color: gold, not marketplace green. */
    --dcm-color-primary:         var(--dcm-concierge-accent);

    /* Tenant brand token also re-routed to gold — covers legacy components
       that read --dct directly, bypassing --dcm-color-primary. */
    --dct--customized-colors-buttonsLinks: var(--dcm-concierge-accent);

    /* Canvas + surfaces — forest ramp at ~160° hue, low chroma. Canvas sits
       one step lighter than chrome (1% lightness delta); elevation comes
       from content lifting, not background contrast. */
    --dcm-color-body-bg:         var(--dcm-concierge-forest-750);
    --dcm-color-surface:         var(--dcm-concierge-forest-900);
    --dcm-color-surface-hover:   var(--dcm-concierge-forest-700);
    --dcm-color-surface-muted:   var(--dcm-concierge-forest-800);
    --dcm-color-surface-overlay: rgba(0, 0, 0, 0.5);

    /* Ink — cream primary (Admin sidebar echo), forest hierarchy tiers. */
    --dcm-color-text-primary:    var(--dcm-concierge-cream-100);
    --dcm-color-text-secondary:  var(--dcm-concierge-forest-200);
    --dcm-color-text-muted:      var(--dcm-concierge-forest-400);
    --dcm-color-text-inverted:   var(--dcm-concierge-forest-900);

    /* Borders */
    --dcm-color-border:          var(--dcm-concierge-forest-600);
    --dcm-color-border-dark:     var(--dcm-concierge-forest-950);

    /* Focus ring — gold, not the marketplace default blue. */
    --dcm-color-focus:           var(--dcm-concierge-accent);
    --dcm-color-focus-shadow:    var(--dcm-concierge-accent-soft);

    /* Modal — dark surface + gold halo for elevation (drop shadow doesn't
       read on a near-black canvas). */
    --dcm-modal-bg:                  var(--dcm-color-surface);
    --dcm-modal-text-color:          var(--dcm-color-text-primary);
    --dcm-modal-secondary-text-color: var(--dcm-color-text-secondary);
    --dcm-modal-footer-bg:           var(--dcm-color-surface-muted);
    --dcm-modal-footer-text-color:   var(--dcm-color-text-primary);
    --dcm-modal-backdrop-color:      rgba(0, 0, 0, 0.7);
    --dcm-modal-shadow:
      0 0 0 1px var(--dcm-concierge-accent-soft),
      0 0 32px var(--dcm-concierge-accent-glow);

    /* Age Gate — re-declared here (not just at :root) because a CSS custom property's
       var() reference resolves relative to the element it's declared on, not where it's
       inherited to. Declaring these only at :root would freeze them to :root's own
       (light-mode) resolution of --dcm-modal-text-color/etc, since :root itself never
       gets the .concierge-mode class — they'd never pick up the overrides below despite
       inheriting into a .concierge-mode subtree. Re-declaring them here, referencing the
       same tokens, lets them resolve against *this* element's (body's) already-overridden
       values instead. */
    --dcm-age-gate-modal-text-color: var(--dcm-modal-text-color);
    --dcm-age-gate-body-color:       var(--dcm-modal-secondary-text-color);
    --dcm-age-gate-link-color:       var(--dcm-color-primary);

    /* Skeleton — dark shimmer. The shimmer-from/to sources are marketplace
       globals; where they're absent (admin preview) the declarations are
       inert because that markup renders no skeletons. */
    --dcm-skeleton-shimmer-from: var(--dcm-skeleton-shimmer-dark-from);
    --dcm-skeleton-shimmer-to:   var(--dcm-skeleton-shimmer-dark-to);
    --dcm-skeleton-bg:           var(--dcm-color-surface-hover);
    --dcm-skeleton-border-color: var(--dcm-color-border);

    /* Popover — recessed surface (muted < canvas), neutral border + dark
       shadow; item-hover re-declared (marketplace's :root value would
       otherwise leak through). */
    --dcm-popover-bg:            var(--dcm-color-surface-muted);
    --dcm-popover-item-color:    var(--dcm-color-text-primary);
    --dcm-popover-item-hover-bg: var(--dcm-color-surface-hover);
    --dcm-popover-shadow:
      0 0 0 1px var(--dcm-color-border),
      0 8px 24px var(--dcm-concierge-shadow-soft);

    /* Header divider — pin to the concierge border (default uses theme-locked grey). */
    --dcm-header-divider-color:  var(--dcm-color-border);

    /* Product option tiles (add-to-cart modal weight picker). Marketplace
       defaults are light-mode greys; pin to concierge surfaces so the tiles
       blend with the dark modal. Selection border/Add icon already use the
       tenant gold token. */
    --dcm-product-option-bg:                var(--dcm-color-surface-muted);
    --dcm-product-option-text-color:        var(--dcm-color-text-primary);
    --dcm-product-option-border-color:      var(--dcm-color-border);
    --dcm-product-price-strikethrough-color: var(--dcm-color-text-muted);

    /* Product identity in the add-to-cart modal — brand defaults to
       text-secondary (forest-200) and washes out on the dark surface; bump
       brand + name to text-primary. */
    --dcm-product-brand-color:    var(--dcm-color-text-primary);
    --dcm-product-name-color:     var(--dcm-color-text-primary);
    --dcm-product-detail-muted-color: var(--dcm-color-text-secondary);

    /* Effect chips — a quiet accent-derived secondary (hue +90°, chroma
       × 0.20) so the chips hold a fixed relationship to ANY accent, stock
       gold or tenant brand, instead of a hardcoded hue that clashes. The
       ink pins its lightness per scheme (0.90 here; 0.42 in the light
       block) with capped chroma so labels stay readable and never neon.
       Formulas match the remote's standalone fallback (dutchie-cf-agents
       globals.css). Declared at this scope — not :root — so a tenant accent
       override on the same element recomputes all of them. Consumed by the
       concierge remote as bg-effect-tint / border-effect-border /
       text-effect-ink. */
    --dcm-concierge-effect-secondary: oklch(from var(--dcm-concierge-accent) l calc(c * 0.2) calc(h + 90));
    --dcm-concierge-effect-tint:   color-mix(in srgb, var(--dcm-concierge-effect-secondary) 16%, transparent);
    --dcm-concierge-effect-border: color-mix(in srgb, var(--dcm-concierge-effect-secondary) 34%, transparent);
    --dcm-concierge-effect-ink:    oklch(from var(--dcm-concierge-effect-secondary) 0.9 min(c, 0.13) h);

    /* Strain-dot triad — the accent anchors sativa; indica/hybrid flank it
       ±90° (270° = −90°) at 80% chroma so no dot dominates. Solid fills keep
       real chroma so they stay distinguishable at 8px. Same +90° secondary
       offset as the effect chips — one coherent secondary at two
       intensities. cbd stays neutral (remote maps it to text-muted). */
    --dcm-concierge-strain-sativa: oklch(from var(--dcm-concierge-accent) l calc(c * 0.8) h);
    --dcm-concierge-strain-indica: oklch(from var(--dcm-concierge-accent) l calc(c * 0.8) calc(h + 90));
    --dcm-concierge-strain-hybrid: oklch(from var(--dcm-concierge-accent) l calc(c * 0.8) calc(h + 270));

    /* BOGO badge — fixed green semantic (the retired moss-500 value), never
       tenant-derived; same rule as sale/status. */
    --dcm-concierge-bogo: #1f4f40;

    /* Quantity controls pill + circle button — marketplace hardcodes white/grey;
       blend to surface-hover. Hover steps UP a tier (dark-theme affordance =
       "lift toward light"). */
    --dcm-quantity-controls-bg:         var(--dcm-color-surface-hover);
    --dcm-quantity-controls-icon-color: var(--dcm-color-text-primary);
    --dcm-circle-button-bg:             var(--dcm-color-surface-hover);
    --dcm-circle-button-hover-bg:       var(--dcm-concierge-forest-600);

    /* Mobile slide-up (BottomDrawer) — MUI white default → concierge canvas. */
    --dcm-bottom-drawer-bg:         var(--dcm-color-surface);
    --dcm-bottom-drawer-exit-color: var(--dcm-color-text-muted);
`;

// Light-variant deltas, overlaid on the base block. Production scope:
// `.concierge-mode[data-concierge-theme="light"]`.
export const CONCIERGE_LIGHT_SCHEME_CSS = `
    /* Re-resolve the muted-accent blend at this scope so it picks up the
       light variant's --dcm-color-text-secondary instead of the dark one. */
    --dcm-concierge-accent-muted:  color-mix(in oklch, var(--dcm-concierge-accent) 55%, var(--dcm-color-text-secondary));

    /* Primary stays gold — same accent token; tenant overrides flow */
    --dcm-color-primary:         var(--dcm-concierge-accent);

    /* Canvas + surfaces */
    --dcm-color-body-bg:         var(--dcm-concierge-cream-50);
    --dcm-color-surface:         #ffffff;
    --dcm-color-surface-hover:   var(--dcm-concierge-cream-100);
    --dcm-color-surface-muted:   var(--dcm-concierge-cream-100);
    --dcm-color-surface-overlay: rgba(0, 0, 0, 0.5);

    /* Ink — marketplace inks on cream.
       Secondary sits between primary and muted in both lightness and
       contrast (~5.85:1 on white) so body copy passes WCAG AA. */
    --dcm-color-text-primary:    #485055;
    --dcm-color-text-secondary:  #5d666d;
    --dcm-color-text-muted:      #677882;
    --dcm-color-text-inverted:   #ffffff;

    /* Borders */
    --dcm-color-border:          var(--dcm-concierge-cream-200);
    --dcm-color-border-dark:     #2c2c2c;

    /* Modal — light defaults. Restore --dcm-modal-shadow explicitly or the
       dark-mode halo leaks through via cascade. */
    --dcm-modal-bg:                  var(--dcm-color-surface);
    --dcm-modal-text-color:          var(--dcm-color-text-primary);
    --dcm-modal-secondary-text-color: var(--dcm-color-text-secondary);
    --dcm-modal-footer-bg:           var(--dcm-color-surface-muted);
    --dcm-modal-footer-text-color:   var(--dcm-color-text-primary);
    --dcm-modal-backdrop-color:      rgba(0, 0, 0, 0.5);
    --dcm-modal-shadow:              0 5px 15px 0 rgba(0, 0, 0, 0.14);

    /* Age Gate — same re-declaration rationale as the dark block above. */
    --dcm-age-gate-modal-text-color: var(--dcm-modal-text-color);
    --dcm-age-gate-body-color:       var(--dcm-modal-secondary-text-color);
    --dcm-age-gate-link-color:       var(--dcm-color-primary);

    /* Skeleton — light shimmer */
    --dcm-skeleton-shimmer-from: rgba(0, 0, 0, 0.06);
    --dcm-skeleton-shimmer-to:   rgba(0, 0, 0, 0.1);
    --dcm-skeleton-bg:           var(--dcm-color-surface-hover);
    --dcm-skeleton-border-color: var(--dcm-color-border);

    /* Effect chip ink — same secondary, deepened (L 0.42) for readability
       on the pale light tint. */
    --dcm-concierge-effect-ink:  oklch(from var(--dcm-concierge-effect-secondary) 0.42 min(c, 0.13) h);
`;

// Mid-tone canvases reuse the light scheme wholesale — surfaces
// still lift toward white, which is the right direction for near-black text —
// and override only the two inks. Declared after the light statics so the
// escalated values win by source order at equal specificity.
export const CONCIERGE_LIGHT_MAX_CONTRAST_SCHEME_CSS = `
${CONCIERGE_LIGHT_SCHEME_CSS}
    /* Escalated ink: no brand ink clears 4.5:1 on a mid-tone canvas. */
    --dcm-color-text-primary:    var(--dcm-concierge-forest-950);
    --dcm-color-text-secondary:  var(--dcm-concierge-forest-600);
`;

// Canvas-seeded surface derivation — dark scheme. The consumer declares
// --dcm-concierge-canvas itself (production interpolates the validated hex;
// the admin preview sets it inline). Mixes use oklab, NOT oklch: browsers
// treat a near-neutral seed's oklch hue as powerless, and mixing two
// powerless hues yields hue "none" (rendered as 0° — pink). Rectangular
// oklab has no hue channel to lose, and its lightness math matches, so the
// luminance thresholds are unaffected.
//
// Surfaces step down/up from the seed the same way the forest ramp steps
// around forest-750. Text-primary is deliberately NOT derived — it is chosen
// per-canvas by resolveConciergeTheme, which escalates to forest-950 when no
// brand ink clears AA. Nothing upstream constrains the canvas's luminance, so
// cream's contrast is not guaranteed here.
export const CONCIERGE_DARK_CANVAS_DERIVATION_CSS = `
    --dcm-color-body-bg: var(--dcm-concierge-canvas);
    --dcm-color-surface: color-mix(in oklab, var(--dcm-concierge-canvas) 80%, black);
    --dcm-color-surface-hover: color-mix(in oklab, var(--dcm-concierge-canvas) 92%, white);
    --dcm-color-surface-muted: color-mix(in oklab, var(--dcm-concierge-canvas) 88%, black);
    --dcm-color-text-secondary: color-mix(in oklab, var(--dcm-concierge-canvas) 45%, white);
    --dcm-color-text-muted: color-mix(in oklab, var(--dcm-concierge-canvas) 62%, white);
    --dcm-color-text-inverted: color-mix(in oklab, var(--dcm-concierge-canvas) 80%, black);
    --dcm-color-border: color-mix(in oklab, var(--dcm-concierge-canvas) 84%, white);
    --dcm-color-border-dark: color-mix(in oklab, var(--dcm-concierge-canvas) 70%, black);
    --dcm-circle-button-hover-bg: var(--dcm-color-border);
`;

// Canvas-seeded surface derivation — light scheme. Seed is the page
// background. Up-steps are capped hard by the white ceiling — a near-white
// seed leaves almost no headroom — so unlike the dark block, only the card
// surface mixes toward white (an even split, for the biggest step that still
// carries the hue); hover/muted/borders take their separation downward
// instead, as progressively deeper shades of the seed's own hue.
// Surface-hover doubles as the product thumbnail bed. Fixed dark inks.
export const CONCIERGE_LIGHT_CANVAS_DERIVATION_CSS = `
    --dcm-color-body-bg: var(--dcm-concierge-canvas);
    --dcm-color-surface: color-mix(in oklab, var(--dcm-concierge-canvas) 50%, white);
    --dcm-color-surface-hover: color-mix(in oklab, var(--dcm-concierge-canvas) 92%, black);
    --dcm-color-surface-muted: color-mix(in oklab, var(--dcm-concierge-canvas) 96%, black);
    --dcm-color-border: color-mix(in oklab, var(--dcm-concierge-canvas) 88%, black);
`;
